import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  UserPlusIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import {
  useStakeholderGroup,
  useGroupMembers,
  useAddGroupMember,
  useRemoveGroupMember,
  useGroupObjects,
  useUpdateNotificationPreferences
} from '../../hooks/useStakeholderGroups';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import NotificationSettings from '../../components/stakeholders/NotificationSettings';
import { getSession } from '../../api/supabase';
import { safeFetch } from '../../utils/requestUtils';
import { useUI } from '../../contexts/UIContext';

/**
 * Stakeholder Group Detail Page
 * Shows group details, members, and assigned data objects
 */
const StakeholderGroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { updateBreadcrumbs, showNotification } = useUI();

  const { data: group, isLoading: groupLoading, error: groupError } = useStakeholderGroup(groupId);
  const { data: members, isLoading: membersLoading, refetch: refetchMembers } = useGroupMembers(groupId);
  const { data: assignedObjects, isLoading: objectsLoading } = useGroupObjects(groupId);
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const updateNotificationPreferences = useUpdateNotificationPreferences();

  // Debug logging
  console.log('StakeholderGroupDetailPage - Debug:', {
    groupId,
    group,
    groupLoading,
    groupError,
    members,
    membersLoading,
    assignedObjects,
    objectsLoading
  });

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRole, setSelectedRole] = useState('member');

  const handleBack = () => {
    navigate('/admin/stakeholder-groups');
  };

  const searchUsers = async () => {
    if (!searchEmail.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const session = await getSession();
      const token = session?.access_token;

      // Fetch all users and filter client-side
      const response = await safeFetch('/admin/users', {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const allUsers = response.data || [];

      // Filter users by email or name
      const filtered = allUsers.filter(user => {
        const searchLower = searchEmail.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();

        return email.includes(searchLower) ||
               firstName.includes(searchLower) ||
               lastName.includes(searchLower);
      });

      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async (userId) => {
    try {
      await addMember.mutateAsync({
        groupId,
        userId,
        role: selectedRole
      });

      setShowAddMemberModal(false);
      setSearchEmail('');
      setSearchResults([]);
      showNotification('Member added successfully', 'success');
      refetchMembers();
    } catch (error) {
      console.error('Failed to add member:', error);
      showNotification(`Failed to add member: ${error.message}`, 'error');
    }
  };

  const handleRemoveMemberClick = (userId, userName) => {
    setRemovingMember({ userId, userName });
    setShowRemoveConfirm(true);
  };

  const handleRemoveMemberConfirm = async () => {
    if (!removingMember) return;

    try {
      await removeMember.mutateAsync({
        groupId,
        userId: removingMember.userId
      });
      showNotification('Member removed successfully', 'success');
      refetchMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
      showNotification(`Failed to remove member: ${error.message}`, 'error');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleUpdateNotificationPreferences = async (preferences) => {
    try {
      await updateNotificationPreferences.mutateAsync({
        groupId,
        preferences
      });
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      throw error; // Re-throw to let NotificationSettings component handle the error
    }
  };

  // Update breadcrumbs when group data is loaded
  useEffect(() => {
    if (group) {
      updateBreadcrumbs([
        { name: 'Admin', href: '/admin' },
        { name: 'Stakeholder Groups', href: '/admin/stakeholder-groups' },
        { name: group.group_name }
      ]);
    }
  }, [group, groupId, updateBreadcrumbs]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchEmail.trim()) {
        searchUsers();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchEmail]);

  if (groupLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (groupError || !group) {
    return (
      <div className="alert alert-error">
        <span>Error loading stakeholder group: {groupError?.message || 'Group not found'}</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="btn btn-ghost btn-sm mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Stakeholder Groups
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 mr-3 text-primary" />
            <div>
              <h1 className="text-2xl font-semibold text-secondary-900">{group.group_name}</h1>
              <p className="text-sm text-secondary-500 mt-1">
                <span className="capitalize">{group.group_type}</span> · Created {new Date(group.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {group.description && (
          <p className="mt-4 text-secondary-700">{group.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Members Section */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">
                Members ({members?.length || 0})
              </h2>
              <button
                onClick={() => setShowAddMemberModal(true)}
                className="btn btn-primary btn-sm"
              >
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Add Member
              </button>
            </div>

            <div className="p-6">
              {membersLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : !members || members.length === 0 ? (
                <div className="text-center py-8 text-secondary-500">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-secondary-300" />
                  <p>No members yet</p>
                  <p className="text-sm">Add users to this stakeholder group</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100"
                    >
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                          {member.full_name?.charAt(0) || member.email?.charAt(0) || '?'}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-secondary-900">
                            {member.full_name || 'Unknown User'}
                          </p>
                          <p className="text-sm text-secondary-600">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="badge badge-sm badge-secondary capitalize">
                          {member.role || 'member'}
                        </span>
                        <button
                          onClick={() => handleRemoveMemberClick(member.user_id, member.full_name || member.email)}
                          className="btn btn-ghost btn-sm text-error hover:bg-error hover:text-white"
                          title="Remove member"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Assigned Objects */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h2 className="text-lg font-semibold text-secondary-900">
                Assigned Data Objects ({assignedObjects?.length || 0})
              </h2>
            </div>

            <div className="p-6">
              {objectsLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : !assignedObjects || assignedObjects.length === 0 ? (
                <div className="text-center py-4 text-secondary-500">
                  <p className="text-sm">No data objects assigned</p>
                  <p className="text-xs mt-1">Assign this group to data objects from the lineage page</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assignedObjects.map((obj) => (
                    <div
                      key={obj.data_object_id}
                      className="p-3 bg-secondary-50 rounded hover:bg-secondary-100"
                    >
                      <p className="font-medium text-secondary-900 text-sm truncate">
                        {obj.object_name || obj.data_object_id}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="badge badge-xs capitalize">
                          {obj.relationship_type || 'consumer'}
                        </span>
                        <span className={`badge badge-xs ${
                          obj.business_impact_level === 'critical' ? 'badge-error' :
                          obj.business_impact_level === 'high' ? 'badge-warning' :
                          obj.business_impact_level === 'medium' ? 'badge-info' :
                          'badge-ghost'
                        }`}>
                          {obj.business_impact_level || 'low'} impact
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Settings Section */}
      <div className="mt-6">
        <NotificationSettings
          group={group}
          onUpdate={handleUpdateNotificationPreferences}
        />
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal modal-open">
          <div className="modal-box bg-white">
            <h3 className="font-bold text-lg text-secondary-900 mb-4">Add Member</h3>

            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text text-secondary-900">Search by Email</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="input input-bordered bg-white text-secondary-900 w-full pr-10"
                  placeholder="Type to search users..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                />
                <MagnifyingGlassIcon className="h-5 w-5 absolute right-3 top-3 text-secondary-400" />
              </div>
            </div>

            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text text-secondary-900">Role</span>
              </label>
              <select
                className="select select-bordered bg-white text-secondary-900"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="lead">Lead</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            {searching ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium text-secondary-700 mb-2">Search Results:</p>
                {searchResults.map((user) => {
                  const isAlreadyMember = members?.some(m => m.user_id === user.id);
                  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown';
                  return (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-3 rounded ${
                        isAlreadyMember ? 'bg-secondary-100' : 'bg-secondary-50 hover:bg-secondary-100'
                      }`}
                    >
                      <div>
                        <p className="font-medium text-secondary-900">{fullName}</p>
                        <p className="text-sm text-secondary-600">{user.email}</p>
                      </div>
                      {isAlreadyMember ? (
                        <span className="badge badge-sm">Already member</span>
                      ) : (
                        <button
                          onClick={() => handleAddMember(user.id)}
                          className="btn btn-primary btn-sm"
                          disabled={addMember.isPending}
                        >
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : searchEmail.trim() ? (
              <div className="text-center py-4 text-secondary-500">
                <p className="text-sm">No users found</p>
              </div>
            ) : null}

            <div className="modal-action">
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSearchEmail('');
                  setSearchResults([]);
                }}
                className="btn btn-ghost"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false);
          setRemovingMember(null);
        }}
        onConfirm={handleRemoveMemberConfirm}
        title="Remove Member"
        message={`Are you sure you want to remove ${removingMember?.userName} from this group?`}
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
};

export default StakeholderGroupDetailPage;
